import { FileUploadStatus, FinalFileUploadStatus } from '@shared/types'
import { OpenModalEvent } from '@ui-common/components/Modal'
import { getCancelEditingPromptModalEvent } from '@ui-common/components/Modal/CancelEditingModal'
import type { FileObject } from '@ui-components/types'
import { FieldValues, get } from 'react-hook-form'
import { IntlShape } from 'react-intl'

const filterForSuccessAndNeedStatusCheckFiles = (
  fileObjects: FileObject[],
): { successFiles: FileObject[]; needStatusCheckFiles: FileObject[] } => {
  return fileObjects.reduce<{
    successFiles: FileObject[]
    needStatusCheckFiles: FileObject[]
  }>(
    (result, file) => {
      const fileUploadedSuccessfully = file.uploadProgress === 100 && !file.error && !!file.fileId && file.isValid
      const fileUploadInProgress =
        file.uploadProgress !== 100 && file.uploadProgress !== undefined && !file.error && !!file.fileId

      if (fileUploadedSuccessfully) {
        result.successFiles.push(file)
      } else if (fileUploadInProgress) {
        result.needStatusCheckFiles.push(file)
      }
      return result
    },
    { successFiles: [], needStatusCheckFiles: [] },
  )
}

/**
 * Check FileObject array for file with incomplete upload status, poll for latest result and update them.
 * **/
const getUpdatedFilesFromFormData = async (
  fileObjects: FileObject[] | undefined,
  getFileUploadStatus: (id: string, abortSignal?: AbortSignal) => Promise<FileUploadStatus>,
  abortSignal?: AbortSignal,
): Promise<FileObject[] | undefined> => {
  if (fileObjects === undefined) {
    return undefined
  }
  const { successFiles, needStatusCheckFiles } = filterForSuccessAndNeedStatusCheckFiles(fileObjects)
  const result: FileObject[] = [...successFiles]

  for (const item of needStatusCheckFiles) {
    if (item?.fileId) {
      const status = await getFileUploadStatus(item.fileId, abortSignal)

      if (status === FinalFileUploadStatus.UploadCompleted) {
        result.push({ ...item, uploadProgress: 100 })
      }
    }
  }

  return result
}

const openCancelEditingPromptModal = ({
  intl,
  onConfirm,
  openModal,
}: {
  intl: IntlShape
  onConfirm: () => void
  openModal: (modalEvent: OpenModalEvent) => void
}): void => {
  openModal(
    getCancelEditingPromptModalEvent({
      onConfirm,
      intl,
      header: intl.formatMessage({
        id: 'common-back-to-application-unsaved-header',
      }),
      text: intl.formatMessage(
        {
          id: 'common-back-to-application-unsaved-warning',
        },
        { continue_action: intl.formatMessage({ id: 'common-continue-anyway' }) },
      ),
    }),
  )
}

const isUploading = <TFormData extends FieldValues>(names: string[], data: TFormData): boolean => {
  return names.some((name) => {
    if (get(data, name)) {
      const uploadingArray = (get(data, name) as FileObject[]).filter(
        (item: FileObject) => item.uploadProgress !== undefined && item.uploadProgress !== 100,
      )

      return uploadingArray?.length !== 0
    }
    return false
  })
}

const extractFileIds = (files: FileObject[] | undefined): string[] => {
  if (files === undefined) {
    return []
  }
  return files.map((file) => file.fileId).filter((id) => id !== undefined)
}

export {
  extractFileIds,
  filterForSuccessAndNeedStatusCheckFiles,
  getUpdatedFilesFromFormData,
  isUploading,
  openCancelEditingPromptModal,
}
